import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Container from "../components/Container";
import ErrorMessageBlock from "../components/ErrorMessageBlock";
import Header from "../components/Header";
import RippleLoader from "../components/RippleLoader";
import NavigationButtons from "../components/Switch/NavigationButtons";
import SwitchingForm from "../components/Switch/SwitchingForm";
import { FUEL_FLAG_MAPPING } from "../constants/fuelFlag";
import { normaliseFuelType, SUPPLIER_FUEL_TYPE } from "../constants/tariffInfo";
import { useSwitch } from "../context/SwitchContext";
import { useTariff } from "../context/TariffContext";
import { useUserInfo } from "../context/UserInfoContext";
import EN from "../locales/en";
import createSwitchingFormYupSchema from "../utils/createSwitchingFormYupSchema";
import {
  addConfirmEmailToSchema,
  addPaymentInfoToSchema,
  convertStringsToBooleans,
  generateOverrideData,
  generateSwitchingFormInitialValues,
  setMpanMprnRequired,
} from "../utils/switching";

const Switch = () => {
  const navigate = useNavigate();

  const { mpan, mprn, priceId } = useParams();

  const { combinedTariffInfo, quoteId, meterDetails } = useTariff();
  const { emailAddress, firstName, lastName, setShowAddressPageError } =
    useUserInfo();

  const {
    switchingFormData,
    setSwitchingFormData,
    setPriceId,
    generateSwitch,
    generatingSwitch,
    getSwitchValidationSchema,
    fetchingValidationSchema,
    selectedSwitchTariff,
    showSwitchingError,
    subscribeContactToEmail,
  } = useSwitch();

  const [switchValidationSchema, setSwitchValidationSchema] = useState(null);
  const [formattedYupValidationSchema, setFormattedYupValidationSchema] =
    useState(null);
  const [supplierTermsInfo, setSupplierTermsInfo] = useState(null);

  const dnoId = combinedTariffInfo?.dnoArea ?? meterDetails?.mpan?.slice(0, 2);
  const currentSupplierId =
    normaliseFuelType(combinedTariffInfo?.fuelType) === SUPPLIER_FUEL_TYPE.GAS
      ? parseInt(combinedTariffInfo?.existingSupplieIdGas)
      : parseInt(combinedTariffInfo?.existingSupplieIdElec);

  const switchMode =
    currentSupplierId === parseInt(selectedSwitchTariff?.supplierId)
      ? "retention"
      : "acquisition";

  useEffect(() => {
    if (!mpan || !priceId || !meterDetails) {
      setShowAddressPageError(true);
      navigate("/address");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (priceId) {
      setPriceId(priceId);
    }
  }, [priceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (switchValidationSchema) {
      // From the validation schema, build up the default state
      // for the switching form
      const initialFormValues = generateSwitchingFormInitialValues(
        switchValidationSchema,
        supplierTermsInfo
      );

      // add in the information about the user that we already have
      const overrideData = generateOverrideData(
        quoteId,
        priceId,
        initialFormValues,
        meterDetails,
        dnoId,
        emailAddress,
        firstName,
        lastName,
        selectedSwitchTariff
      );

      setSwitchingFormData({
        ...initialFormValues,
        ...overrideData,
      });

      const yupSchema = createSwitchingFormYupSchema(
        // add in custom checks for confirm email and payment info
        setMpanMprnRequired(
          addConfirmEmailToSchema(
            addPaymentInfoToSchema(switchValidationSchema)
          ),
          FUEL_FLAG_MAPPING[normaliseFuelType(selectedSwitchTariff?.fuelType)]
        )
      );
      setFormattedYupValidationSchema(yupSchema);
    }
  }, [switchValidationSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchSchema = async () => {
      const response = await getSwitchValidationSchema(switchMode);
      setSwitchValidationSchema(response?.switchValidationSchema);
      setSupplierTermsInfo(response?.otherInfo);
    };

    fetchSchema();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (values) => {
    try {
      const valuesToSubmit = JSON.parse(JSON.stringify(values)); // deep copy
      delete valuesToSubmit?.paymentInfo?.detailsConfirmed;
      delete valuesToSubmit?.paymentInfo?.accountHolderConfirmed;
      delete valuesToSubmit?.customerInfo?.confirmEmail;
      delete valuesToSubmit?.psr?.hasVulnerabilities;
      // rename valuesToSubmit?.psr?.options to valuesToSubmit?.psr?.vulnerabilities to match Perse
      if (valuesToSubmit?.psr?.options) {
        valuesToSubmit.psr.vulnerabilities = valuesToSubmit.psr.options;
        delete valuesToSubmit.psr.options;
      }

      //subscribe user to email if they have consented
      if (
        valuesToSubmit?.consentInfo?.hasConsentSupplierMarketingEmail === "true"
      ) {
        await subscribeContactToEmail();
      }

      convertStringsToBooleans(valuesToSubmit);

      const switchStatus = await generateSwitch(valuesToSubmit);

      if (switchStatus?.switchId) {
        navigate(`/switch-status/${switchStatus?.switchId}`);
      }
    } catch (errors) {
      console.error(errors);
    }
  };

  return (
    <Container>
      {showSwitchingError && (
        <ErrorMessageBlock
          errorDetails="Failed to generate switch."
          message="Please review your answers and try again."
        />
      )}
      <Header title={EN.switch.title} subtitle={EN.switch.subtitle} />
      {fetchingValidationSchema ? (
        <div className="mt-6 flex justify-center">
          <RippleLoader />
        </div>
      ) : (
        <Formik
          initialValues={switchingFormData}
          validationSchema={formattedYupValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, isSubmitting }) => (
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            >
              <SwitchingForm
                initialValues={switchingFormData}
                supplierName={selectedSwitchTariff?.supplierName}
                values={values}
                switchValidationSchema={switchValidationSchema}
                supplierTermsInfo={supplierTermsInfo}
                isSubmitting={isSubmitting}
              />
              <NavigationButtons
                generatingSwitch={generatingSwitch}
                priceId={priceId}
                mpan={mpan}
                mprn={mprn}
              />
            </Form>
          )}
        </Formik>
      )}
    </Container>
  );
};

export default Switch;
